/** What more than one tab of the Sources screen needs and no component owns: the six census states
 * and their words, the life-basis sentences, the stream's name, the period cell label, and how one
 * tab asks another to put focus on a row.
 *
 * Moved out of `Sources.tsx` when the screen became three tabs, because the Coverage grid and the
 * register both draw the **same six states** and two copies of a word-and-glyph table is how a
 * seventh state, or a renamed one, ends up on one tab and not the other. Nothing here changed in
 * meaning; the comments are the originals.
 */

import {
  CheckCircle,
  Clock,
  Minus,
  MinusCircle,
  Question,
  WarningCircle,
} from "@phosphor-icons/react";
import type { Icon } from "@phosphor-icons/react";
import { createContext } from "react";

import type { SourceCoverage, SourceLinks, SourceWindow } from "../api/sources";

/** How one tab of the Sources screen sends the reader to a row on another (EXP-06).
 *
 * A connection used to be a scroll on one page; with three tabs the target may not be mounted yet,
 * so the screen owns the move (switch tab, then focus) and the parts only ask. `openStream` answers
 * `false` when the stream's row is not among those listed (a filter, a ceiling) so the caller can
 * *say* that instead of doing nothing - which reads as a broken link. `openAccount` cannot know
 * ahead of the tab switch, so it answers `true` once the move is queued. */
export interface SourcesNav {
  openStream(sourceId: string): boolean;
  openAccount(accountId: string): boolean;
}
export const SourcesNavContext = createContext<SourcesNav | null>(null);

/** What each state is called on screen, and what it means to a reader.
 *
 * The long forms are the point of the map. "Missing" and "unknown" are one word apart and a whole
 * decision apart: one is somebody's to chase and the other is nobody's, because an event-driven
 * source with nothing in it may simply mean nothing happened.
 */
export const STATE: Record<
  string,
  { short: string; long: string; glyph: Icon; weight: "regular" | "fill" }
> = {
  // **One glyph per state, and no two alike** (F-040: a state must not depend on colour alone, and
  // the word stays beside the glyph because a glyph alone is a second thing to learn). `fill` marks
  // a state that is *resolved* - data is in, or a person said there was nothing - and `regular`
  // one that is still open or outside the census's remit.
  submitted: {
    short: "submitted",
    long: "activity arrived for this expected-bill period; this is not a verification result",
    glyph: CheckCircle,
    weight: "fill",
  },
  declared_nil: {
    short: "confirmed none",
    long: "somebody stated there was no activity, with a reason",
    glyph: MinusCircle,
    weight: "fill",
  },
  missing: {
    short: "missing",
    long: "it should have arrived by now and has not",
    glyph: WarningCircle,
    weight: "regular",
  },
  pending: {
    short: "pending",
    long: "the period has not closed yet",
    glyph: Clock,
    weight: "regular",
  },
  unknown: {
    short: "unknown",
    long: "an on-event source with nothing in it — nothing may have happened, and we cannot tell",
    glyph: Question,
    weight: "regular",
  },
  not_expected: {
    short: "not expected",
    long: "outside this source's life, or out of season",
    glyph: Minus,
    weight: "regular",
  },
};

/** Which of four rows ended a source's life, and **where the remedy is** — CTX-03, ADR-0062.
 *
 * `life_to` is derived on every read as the intersection of the declaration's dates, the meter's,
 * its supply account's and the site's, and `life_to_basis` says which one closed it. A date with no basis would make
 * three different acts look alike, which is the defect `due_date_basis` exists to prevent one
 * module over: a reader told only that nothing is expected after June goes and corrects the stream
 * when the fact to correct is the supply point, the account over it, or the whole site.
 *
 * **Not the same field as `expected_to`**, which is still printed beside `is_retired` as this
 * stream's own retirement. One is what a person said about this row; this is what the product
 * concludes from four.
 */
export const LIFE_BASIS: Record<string, { said: string; remedy: string }> = {
  declaration: {
    said: "this stream was retired",
    remedy: "the stream's own dates are what to change",
  },
  meter: {
    said: "the meter it reports was retired",
    remedy: "the supply point is what to change, on the Organisation screen",
  },
  supply_account: {
    said: "the account that meter is billed under was retired",
    remedy: "the account is what to change, under Who bills what below",
  },
  site: {
    said: "the site was closed",
    remedy: "the site's closure date is what to change, on the Organisation screen",
  },
};

export const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/** What this stream is called, written once.
 *
 * The row head and the owner control's accessible name are the same stream, and two expressions
 * building that name eventually disagree — at which point a screen reader hears one source and a
 * sighted reader sees another.
 */
/** Take the reader to a row on this same screen and put focus on it (EXP-06).
 *
 * The registers and the census are one screen, so a connection is a jump rather than a route; the
 * row is focusable (`tabIndex={-1}`) so a keyboard reader lands on it and a screen reader announces
 * it. Returns false when the row is not drawn - a filtered-out stream, a ceiling - so the caller can
 * say that instead of doing nothing, which reads as a broken link. */
export function jumpTo(id: string): boolean {
  const target = document.getElementById(id);
  if (!target) return false;
  target.scrollIntoView?.({ block: "center" });
  target.focus();
  return true;
}

export function nameOf(source: SourceCoverage): string {
  return source.label ?? `${source.facility_name} — ${source.fuel_or_carrier}`;
}

/** Which period a cell is, in as few characters as will fit twelve of them on a line.
 *
 * Paired with the state word rather than replacing it. An earlier version showed the month name
 * alone for a monthly source, which left the state carried **only by colour** — and the legend
 * three panels up. That is the thing this screen exists not to do.
 */
export function period(window: SourceWindow, cadence: string): string {
  const start = new Date(`${window.period_start}T00:00:00Z`);
  if (cadence === "monthly") return MONTHS[start.getUTCMonth()] ?? window.period_start;
  if (cadence === "quarterly") return `Q${Math.floor(start.getUTCMonth() / 3) + 1}`;
  if (cadence === "annual") return String(start.getUTCFullYear());
  return "any time";
}


/** A reading belongs under every window it overlaps (the source page). Dates are ISO
 * `YYYY-MM-DD`, which order as strings, so no `Date` (and no time zone) is involved. A straddling
 * reading is under both windows; how its quantity divides between them is the inventory's business. */
export function overlapping(
  activities: SourceLinks["activities"],
  window: Pick<SourceWindow, "period_start" | "period_end">,
): SourceLinks["activities"] {
  return activities.filter(
    (activity) => activity.period_start <= window.period_end && activity.period_end >= window.period_start,
  );
}

/** The six census states in the order a reader meets them, for any list that counts them. */
export const STATE_ORDER = [
  "submitted",
  "declared_nil",
  "missing",
  "pending",
  "unknown",
  "not_expected",
] as const;

/** Windows per state. Derived from `windows`, which is the census's own answer, and not from the
 * row's summary counts: those omit `not_expected`, and a tally that left a state out would read as
 * the state not existing. */
export function countStates(windows: readonly SourceWindow[]): Record<string, number> {
  const counts: Record<string, number> = {};
  for (const window of windows) counts[window.state] = (counts[window.state] ?? 0) + 1;
  return counts;
}

/** The date a site's closure ended any of its sources' lives, or `null`.
 *
 * `life_to_basis === "site"` is the census saying *the site was closed* - the only place the API
 * serves a site's end date. The earliest such date is the closure; a source whose life ended for
 * another reason says nothing about the site. */
export function siteClosure(sources: readonly SourceCoverage[]): string | null {
  const dates = sources
    .filter((source) => source.life_to_basis === "site" && source.life_to)
    .map((source) => source.life_to as string)
    .sort();
  return dates[0] ?? null;
}
